import { fr } from 'date-fns/locale';
import { PLATFORM_ID } from '@angular/core';
import { formatDistanceToNow } from 'date-fns';
import { Subject, take, takeUntil, fromEvent, merge, Subscription } from 'rxjs';
import { Component, ElementRef, AfterViewInit, ViewChild, Inject, NgZone, OnDestroy } from '@angular/core';

import { MobileReputationService } from '@sifca-monorepo/clients';

import { MobileAuthService } from '../../services/auth.service';
import { MobileWalletService } from '../../services/wallet.service';
import { reputationPicture } from '../../environments/environment';

@Component({
  template: '',
})
export class MobileWalletComponent implements AfterViewInit, OnDestroy {
  @ViewChild('txnPreloader', { static: false }) preloaderEl!: ElementRef<HTMLElement>;

  private userInteracted = false;
  private interactionSub?: Subscription;
  private preloaderObserver?: IntersectionObserver;
  private unsubscribeAll: Subject<any> = new Subject<any>();

  loyaltySettings: any;
  transactions: any[] = [];
  isLoading: boolean = false;
  reputationPicture = reputationPicture;
  currentUser$ = this.authService.currentUser$;
  wallet$ = this.reputationService.quantitativeWallet$;
  transactions$ = this.transactionsService.transactions$;
  transactionsCount$ = this.transactionsService.transactionsCount$;
  loadingTransactions$ = this.transactionsService.loadingTransactions$;

  constructor(
    protected ngZone: NgZone,
    protected authService: MobileAuthService,
    protected transactionsService: MobileWalletService,
    protected reputationService: MobileReputationService,
    @Inject(PLATFORM_ID) protected platformId: any,
  ) {
    this.transactionsService.transactions$.pipe(takeUntil(this.unsubscribeAll)).subscribe((transactions) => {
      if (transactions) {
        this.transactions.push(...this.reduceTransactions(transactions));
      }
    });
    this.reputationService.loyaltySettings$.pipe(takeUntil(this.unsubscribeAll)).subscribe((settings) => {
      this.loyaltySettings = settings;
    });
    // Track loading state to guard duplicate loads
    this.transactionsService.loadingTransactions$.pipe(takeUntil(this.unsubscribeAll)).subscribe((v) => (this.isLoading = !!v));
  }

  ngOnInit() {
    this.transactionsService.getWalletTransactionsByAffectedPaginated().pipe(takeUntil(this.unsubscribeAll)).subscribe();
  }
  // IntersectionObserver-based infinite scroll (like Discover component)

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      const scroll$ = fromEvent(window, 'scroll');
      const wheel$ = fromEvent(window, 'wheel');
      const touch$ = fromEvent(window, 'touchstart');
      this.interactionSub = merge(scroll$, wheel$, touch$)
        .pipe(take(1))
        .subscribe(() => {
          this.userInteracted = true;
          this.setupTransactionsObserver();
        });
    });
  }

  private setupTransactionsObserver(): void {
    if (!this.preloaderEl) {
      setTimeout(() => this.setupTransactionsObserver(), 150);
      return;
    }
    if (this.preloaderObserver) this.preloaderObserver.disconnect();

    const options: IntersectionObserverInit = {
      root: null,
      threshold: 0.1,
      rootMargin: '0px 0px 150px 0px',
    };
    this.preloaderObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.userInteracted && !this.isLoading) {
          this.ngZone.run(() => {
            this.transactionsService.isLast$.pipe(take(1)).subscribe((isLast) => {
              if (!isLast) {
                this.loadMoreTransactions();
              } else {
                this.preloaderObserver?.disconnect();
              }
            });
          });
        }
      });
    }, options);
    this.preloaderObserver.observe(this.preloaderEl.nativeElement);
  }

  loadMoreTransactions() {
    this.transactionsService.isLast$.pipe(take(1)).subscribe((isLast) => {
      if (!isLast) {
        this.isLoading = true;
        this.transactionsService.pageIndex += 1;
        this.transactionsService.getWalletTransactionsByAffectedPaginated(false).subscribe(() => {
          this.isLoading = false;
        });
      }
    });
  }

  reduceTransactions(transactions) {
    const locale = fr;
    return transactions.map((trans) => ({
      ...trans,
      dateDifference: formatDistanceToNow(new Date(trans.createdAt), {
        addSuffix: true,
        locale,
      }),
    }));
  }

  abs(value: number): number {
    return Math.abs(value);
  }

  ngOnDestroy(): void {
    if (this.interactionSub) this.interactionSub.unsubscribe();
    if (this.preloaderObserver) this.preloaderObserver.disconnect();
    this.transactions = [];
    this.transactionsService.transactions$ = null;
    this.transactionsService.pageIndex = 0;
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
  }
}
